import Button from "@material-ui/core/Button"
import { MembershipRoleEnum } from "../../../types/memberships"
import { useTranslations } from "./use-translations"
import { ArrowRight } from "react-feather"
import { useFormik } from "formik"
import { isLimitedMember as getIsLimitedMember, isNormalMember } from "../../utils"
import { useMembershipActions } from "../../hooks/use-membership-actions"
import { makeAllowedAbilities } from "../../../roles/entities/allowed-abilities"
import { makeRestrictedAbilities } from "../../../roles/entities/restricted-abilities"
import { useSnackbar } from "notistack"
import { useHistory } from "react-router-dom"
import { useUrlWithContext } from "../../../hooks/use-url-with-context"
import { useLanguage } from "../../../i18n/use-language"
import { paths } from "../../../paths"
import { MembershipFormData, ResourceAbility, UpdateAbilitiesFn } from "./types"
import { createNewMember } from "./new-member-data"
import { useEffect, ChangeEvent } from "react"
import { createFormikValidate } from "../../../utils/formik"
import { createMembershipFormDataSchema } from "./membership-form-data-schema"

const createInitialState = (language: string): MembershipFormData => ({
  firstname: "",
  lastname: "",
  userEmail: "",
  sendInvitation: false,
  abilitiesToAdd: [],
  abilitiesToRemove: [],
  associations: [],
  abilitiesInResources: [],
  copyPermissionsFrom: undefined,
  language,
})

export const useMembershipFormData = ({
  orgId,
  onAddMemberSuccess,
}: {
  orgId: string
  onAddMemberSuccess?: () => unknown
}) => {
  const language = useLanguage()
  const history = useHistory()
  const translations = useTranslations()
  const { addMembership } = useMembershipActions()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { createPathWithContext } = useUrlWithContext()
  const membersPagePath = createPathWithContext(paths.orgMembers())
  const goToMembersPage = () => history.push(membersPagePath)

  const membershipFormDataSchema = createMembershipFormDataSchema({ translations })
  const formik = useFormik({
    initialValues: createInitialState(language),
    validate: createFormikValidate(membershipFormDataSchema),
    onSubmit: async (formData, formikHelpers) => {
      try {
        const newMemberData = createNewMember(formData)
        const actionResult = await addMembership(orgId, newMemberData)
        if (actionResult.success) {
          formikHelpers.resetForm()
          onAddMemberSuccess?.()
          return enqueueSnackbar(translations.addMemberSuccessMessage, {
            variant: "success",
            autoHideDuration: 5000,
            action: (id) => {
              return (
                <Button
                  color="default"
                  onClick={() => {
                    closeSnackbar(id)
                    goToMembersPage()
                  }}
                  endIcon={<ArrowRight size={12} />}
                  size="small"
                  data-test="view-members-button"
                >
                  {translations.viewMembersButtonLabel}
                </Button>
              )
            },
          })
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        enqueueSnackbar(errorMessage, { variant: "error" })
      }
    },
  })

  const { values, errors, touched, setFieldValue, handleChange, handleSubmit, isSubmitting, validateForm } = formik
  const { sendInvitation, copyPermissionsFrom } = values
  const isFirstnameError = Boolean(errors.firstname) && Boolean(touched.firstname)
  const isUserEmailError = Boolean(errors.userEmail)
  const isStandardMember = isNormalMember({ role: copyPermissionsFrom })
  const isLimitedMember = getIsLimitedMember({ role: copyPermissionsFrom })
  const isPermissionManagerVisible = Boolean(sendInvitation)
  const isCopiedMember = Boolean(copyPermissionsFrom && !isStandardMember && !isLimitedMember)

  const setCopyPermissionsFrom = (nextValue?: string) => setFieldValue("copyPermissionsFrom", nextValue)
  const setAssociations = (nextValue: string[]) => setFieldValue("associations", nextValue)
  const setAbilitiesInResources = (nextValue: ResourceAbility[]) => setFieldValue("abilitiesInResources", nextValue)

  const handleChangeLanguage = (language: string) => setFieldValue("language", language)
  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setFieldValue("userEmail", e.target.value.trim().toLowerCase() || undefined)
  }
  const handleChangeSendInvitation = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target
    setFieldValue("sendInvitation", checked)
    // If the user wants to send an invitation, by default we should set the copyPermissionsFrom to orgMember
    if (checked) setCopyPermissionsFrom(MembershipRoleEnum.orgMember)
    else setCopyPermissionsFrom(undefined)
    validateForm({ ...values, sendInvitation: checked })
  }

  const updateAbilities: UpdateAbilitiesFn = ({ abilitiesToAdd = [], abilitiesToRemove = [] }) => {
    // Only change abilitiesToAdd if the abilitiesToAdd array contains some values that are not in the old value
    const shouldChangeAbilitiesToAdd = abilitiesToAdd.some((a) => !values.abilitiesToAdd.includes(a))
    // Only change abilitiesToRemove if the abilitiesToRemove array contains some values that are not in the old value
    const shouldChangeAbilitiesToRemove = abilitiesToRemove.some((a) => !values.abilitiesToRemove.includes(a))

    if (shouldChangeAbilitiesToAdd) {
      // If the abilities to add have changed, we need to add new abilities to the abilities to add array
      // And also if the abilities to remove array contains some of the abilities in abilitiesToAdd,
      // we need to remove them from the abilities to remove array
      const nextAbilitiesToAdd = makeAllowedAbilities([...values.abilitiesToAdd, ...abilitiesToAdd]).getAbilities()
      const nextAbilitiesToRemove = values.abilitiesToRemove.filter((a) => !nextAbilitiesToAdd.includes(a))
      setFieldValue("abilitiesToAdd", nextAbilitiesToAdd)
      setFieldValue("abilitiesToRemove", nextAbilitiesToRemove)
    }

    if (shouldChangeAbilitiesToRemove) {
      // If the abilities to remove have changed, we need to add new abilities to the abilities to remove array
      // And also if the abilities to add array contains some of the abilities in abilitiesToRemove,
      // we need to remove them from the abilities to add array
      const nextAbilitiesToRemove = makeRestrictedAbilities([
        ...values.abilitiesToRemove,
        ...abilitiesToRemove,
      ]).getAbilities()
      const nextAbilitiesToAdd = values.abilitiesToAdd.filter((a) => !nextAbilitiesToRemove.includes(a))
      setFieldValue("abilitiesToAdd", nextAbilitiesToAdd)
      setFieldValue("abilitiesToRemove", nextAbilitiesToRemove)
    }
  }

  // Everytime isStandardMember or isLimitedMember changes, we need to reset the abilitiesToAdd and abilitiesToRemove
  // And also reset the abilitiesInResources and associations
  useEffect(() => {
    setFieldValue("abilitiesToAdd", [])
    setFieldValue("abilitiesToRemove", [])
    setFieldValue("associations", [])
    setFieldValue("abilitiesInResources", [])
  }, [isStandardMember, isLimitedMember, setFieldValue])

  return {
    errors,
    values,
    handleChange,
    handleChangeEmail,
    handleChangeSendInvitation,
    handleChangeLanguage,
    handleSubmit,
    setCopyPermissionsFrom,
    setAbilitiesInResources,
    setAssociations,
    updateAbilities,
    isCopiedMember,
    isFirstnameError,
    isLimitedMember,
    isPermissionManagerVisible,
    isStandardMember,
    isSubmitting,
    isUserEmailError,
  }
}
